import { Clipboard } from '@angular/cdk/clipboard';
import { DOCUMENT } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges, Inject, ViewEncapsulation } from '@angular/core';
import Swal, { SweetAlertIcon } from 'sweetalert2';

@Component({
  selector: 'kt-swal-alert',
  templateUrl: './swal-alert.component.html',
  styleUrls: ['./swal-alert.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SwalAlertComponent implements OnInit, OnChanges {
  @Input()
  title = 'System Message';

  @Input()
  icon: SweetAlertIcon = 'info';

  @Input()
  iconColor: any = '';

  @Input()
  message: any[];

  @Input()
  data: any;

  @Output()
  confirmed = new EventEmitter<boolean>();

  @Output()
  confirmed2 = new EventEmitter<boolean>();

  @Output()
  reassign = new EventEmitter<boolean>();

  @Output()
  cancelled = new EventEmitter<boolean>();

  @Output()
  cancelled2 = new EventEmitter<boolean>();

  constructor(
    private clipboard: Clipboard,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit() {
    this.message = [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (Object.keys(changes).length == 1 && Object.keys(changes)[0] == 'message') {
      setTimeout(() => {
        this.alert(this.message);
      }, 0);
      return;
    }

    // Avoid alert pop-up if the only change is in `data` (no changes in message, etc)
    if (Object.keys(changes).length == 1 && Object.keys(changes)[0] == 'data') return;

    if (Object.keys(changes)[0] != 'icon' || Object.keys(changes).length > 1) {
      this.alert(this.message);
    }
  }

  private alert(message: any[]) {
    const normalizedMessages = this.getNormalizedMessages(this.message ?? message);
    const showBankAlert = this.shouldShowBankAlert();
    const plainBlockedHtml = !showBankAlert
      ? this.buildBlockedReactivationFromPlainMessages(normalizedMessages)
      : null;

    if (!normalizedMessages.length && !showBankAlert && !plainBlockedHtml && !this.data) {
      return;
    }

    let icon = null;
    let iconColor = null;

    if (normalizedMessages.length || showBankAlert || plainBlockedHtml || !!this.data) {
      let title = null;
      let messages = '<ul>';
      let showCancelButton = false;
      let confirmButtonText = 'OK';
      let cancelButtonColor = '';
      let cancelButtonText = '';
      normalizedMessages.forEach(row => messages += `<li>${row}</li>`);
      messages += '</ul>';

      if (showBankAlert) {
        const alert = this.data['bank_reactivation_alert'];
        const alertMessages = alert?.messages || [];
        const bankReactivation = this.data?.['bank_reactivation'];
        const bankDeactivation = this.data?.['bank_deactivation'];
        const blockedGroups = this.normalizeBlockedGroups(bankReactivation);
        const blockedCount = blockedGroups.length
          ? blockedGroups.reduce((sum: number, group: any) => sum + (group.accounts?.length ?? 0), 0)
          : this.getBlockedBankAccountCount(bankReactivation);
        const deactivatedAccounts = bankDeactivation?.deactivated ?? [];
        const skippedGroups = bankDeactivation?.skipped ?? [];
        const reactivatedCount = bankReactivation?.reactivated_count
          ?? bankReactivation?.reactivated?.length
          ?? 0;

        if (blockedCount > 0 && blockedGroups.length) {
          icon = 'info';
          messages = this.buildBlockedReactivationHtml(blockedGroups, blockedCount);
        } else if (blockedCount > 0 && alertMessages.length) {
          icon = 'info';
          messages = this.buildBlockedReactivationFromAlertMessages(alertMessages, blockedCount);
        } else if (deactivatedAccounts.length || skippedGroups.length) {
          title = alert?.title ?? 'Bank Accounts Deactivated';
          icon = skippedGroups.length ? 'warning' : 'info';
          messages = this.buildDeactivationHtml(deactivatedAccounts, skippedGroups, alertMessages);
        } else if (reactivatedCount > 0) {
          title = alert?.title ?? 'All Active Back';
          icon = 'success';
          messages = `<div class="text-center px-2">${alertMessages[0] ?? 'All bank accounts have been reactivated successfully.'}</div>`;
        } else {
          icon = 'info';
          messages = this.buildAlertMessagesList(alertMessages);
        }
      } else if (plainBlockedHtml) {
        icon = 'info';
        messages = plainBlockedHtml;
      } else if (this.data) {
        // To be refined if there is any other APIs return `failed_records` in data as this is for `affiliate-lead-setting/create` API only
        if (this.data['exist_records'] && this.data['exist_records'].length > 0) {
          title = 'Add Complete';
          icon = 'info';

          if (this.data['exist_records'].length > 0) {
            const copyIcon = `<i class="fas fa-copy copy-icon copy-exist-records"></i><div class="swal2-copy-tooltip">Copied!</div>`;
            let data = `<div class="text-left mx-2 mt-3">Existing Records: ${copyIcon}</div><div class="exist-records-section p-2 m-2"><ul>`;
            this.data['exist_records'].map(row => data += `<li>${row}</li>`);
            data += '</ul></div>';

            messages += data;
          }
        } else if (this.data['failed_records'] && this.data['failed_records'].length > 0) {
          title = 'Add Complete';
          icon = 'info';
          iconColor = '#f27474';

          if (this.data['failed_records'].length > 0) {
            const copyIcon = `<i class="fas fa-copy copy-icon copy-failed-records"></i><div class="swal2-copy-tooltip">Copied!</div>`;
            let data = `<div class="text-left mx-2 mt-3">Failed Records: ${copyIcon}</div><div class="failed-records-section p-2 m-2"><ul>`;
            this.data['failed_records'].map(row => data += `<li>${row}</li>`);
            data += '</ul></div>';

            messages += data;
          }
        }
        else if ((this.data['linked_promotions'] && this.data['linked_promotions'].length > 0) ||
         (this.data['linked_campaigns'] && this.data['linked_campaigns'].length > 0) ||
         (this.data['linked_aff_campaigns'] && this.data['linked_aff_campaigns'].length > 0)) {
          title = 'Linkage Detection';

          if (this.data['linked_promotions'] != undefined && this.data['linked_promotions'].length > 0) {;
            let data = `<div class="text-left mx-2 mt-3">Promotion(s): </div><div class="linked-promotions-section p-2 m-2"><ul>`;
            this.data['linked_promotions'].map(row => data += `<li>${row}</li>`);
            data = data + '</div>';

            messages += data;
          }

          if (this.data['linked_campaigns'] != undefined && this.data['linked_campaigns'].length > 0) {
            let data = `<div class="text-left mx-2 mt-3">Campaign(s): </div><div class="linked-campaigns-section p-2 m-2"><ul>`;
            this.data['linked_campaigns'].map(row => data += `<li>${row}</li>`);
            data = data + '</div>';

            messages += data;
          }

          if (this.data['linked_aff_campaigns'] != undefined && this.data['linked_aff_campaigns'].length > 0) {
            let data = `<div class="text-left mx-2 mt-3">Affiliate Campaign(s): </div><div class="linked-aff-campaigns-section p-2 m-2"><ul>`;
            this.data['linked_aff_campaigns'].map(row => data += `<li>${row}</li>`);
            data = data + '</div>';

            messages += data;
          }

          Swal.fire({
            title: title,
            html: messages,
            icon: 'warning',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Proceed',
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonColor: '#d33',
            cancelButtonText: 'Cancel',
            heightAuto: false
          }).then(result => {
            if (result.isConfirmed) {
              this.confirmed2.emit(true);
            } else if (result.isDenied || result.dismiss) {
              this.cancelled.emit(true);
            }
          });
          // SKIP default dialog
          return;
        }
        else if (this.data['invalid_lead_list'] && this.data['invalid_lead_list'].length > 0) {
          messages = '';
          const copyIcon1 = `<i class="fas fa-copy copy-icon copy-invalid-lead-list"></i><div class="swal2-copy-tooltip copy-invalid-lead-list-tooltip" style="transform: translate(80px, -55px);">Copied!</div>`;
          let data = `<div class="text-center w-100">The following lead(s) cannot be reassigned: </div><div class="text-left mx-2 mt-3">Invalid Leads: ${copyIcon1}</div><div class="failed-records-section p-2 m-2"><ul>`;
          this.data['invalid_lead_list'].map(row => data += `<li>${row}</li>`);

          let isShowConfirmButton = true;
          if (this.data['selected_list_count'].length == this.data['invalid_lead_list'].length) {
            isShowConfirmButton = false;
            data += `</ul></div>`;
          }
          else {
            data += `</ul></div><div class="text-center w-100 mt-2">Do you want to skip these invalid lead(s) and proceed with the reassign?</div>`;
          }

          messages += data;

          Swal.fire({
            title: 'Invalid Lead(s)',
            html: messages,
            icon: 'error',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Proceed',
            showConfirmButton: isShowConfirmButton,
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonColor: '#d33',
            cancelButtonText: 'Cancel',
            didOpen: () => {
              // Bind function to the icon
              const copyIcon = document.querySelector('.copy-invalid-lead-list');
    
              if (copyIcon) {
                const tooltip = document.querySelector('.copy-invalid-lead-list-tooltip') as HTMLElement;
                copyIcon.addEventListener('click', () => {
                  this.copyInvalidLeadList();
    
                  tooltip.style.display = 'block';
                  setTimeout(() => {
                    tooltip.style.display = 'none';
                  }, 2000);
                });
              }
            }
          }).then(result => {
            if (result.isDenied || result.isDismissed) {} 
            else if (result) {
              this.confirmed.emit(true);
            }
          });

          // SKIP default dialog
          return;
        }
        else if ((this.data['leads_exist'] && this.data['leads_exist'].length > 0) || (this.data['member_exist'] && this.data['member_exist'].length > 0)) {
          title = 'Import Complete!';
          icon = 'info';
          iconColor = '#f27474';

          if (this.data['leads_exist'].length > 0) {
            const copyIcon1 = `<i class="fas fa-copy copy-icon copy-leads-exist-records"></i><div class="swal2-copy-tooltip copy-leads-exist-tooltip">Copied!</div>`;
            let data = `<div class="text-left mx-2 mt-3">Duplicate Leads: ${copyIcon1}</div><div class="failed-records-section p-2 m-2"><ul>`;
            this.data['leads_exist'].map(row => data += `<li>${row}</li>`);
            data += '</ul></div>';

            messages += data;
          }

          if (this.data['member_exist'].length > 0) {
            const copyIcon2 = `<i class="fas fa-copy copy-icon copy-member-exist-records"></i><div class="swal2-copy-tooltip copy-member-exist-tooltip">Copied!</div>`;
            let data = `<div class="text-left mx-2 mt-3">Existing Member Phone Number: ${copyIcon2}</div><div class="failed-records-section p-2 m-2"><ul>`;
            this.data['member_exist'].map(row => data += `<li>${row}</li>`);
            data += '</ul></div>';

            messages += data;
          }
        }
        else if (this.data['leads_exist'] && this.data['leads_exist'].length == 0 && this.data['member_exist'] && this.data['member_exist'].length == 0) {
          title = 'Import Complete!';
          icon = 'info';

          Swal.fire({
            title: title,
            html: messages,
            icon: 'info',
          }).then(result => {
            if (result) {
              this.confirmed.emit(true);
            }
          });

          // SKIP default dialog
          return;
        }
        else if (this.data['invalid_phone_list']?.length > 0) {
          messages = '';
          const copyIcon1 = `<i class="fas fa-copy copy-icon copy-invalid-phone-records"></i><div class="swal2-copy-tooltip copy-invalid-phone-list-tooltip" style="transform: translate(153px, -55px);">Copied!</div>`;
          let data = `<div class="text-center w-100">The following phone number(s) have an invalid format and cannot be imported: </div><div class="text-left mx-2 mt-3">Invalid Phone Numbers: ${copyIcon1}</div><div class="failed-records-section p-2 m-2"><ul>`;
          this.data['invalid_phone_list'].map(row => data += `<li>${row}</li>`);
         

          let isShowConfirmButton = true;
          if (this.data['is_all_invalid'] == true) {
            isShowConfirmButton = false;
            data += `</ul></div>`;
          }
          else {
            data += `</ul></div><div class="text-center w-100 mt-2">Do you want to skip these invalid number(s) and proceed with the import?</div>`;
          }

          messages += data;

          Swal.fire({
            title: 'Invalid Phone Number',
            html: messages,
            icon: 'error',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Proceed',
            showConfirmButton: isShowConfirmButton,
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonColor: '#d33',
            cancelButtonText: 'Cancel',
            didOpen: () => {
              // Bind function to the icon
              const copyIcon = document.querySelector('.copy-invalid-phone-records');
    
              if (copyIcon) {
                const tooltip = document.querySelector('.copy-invalid-phone-list-tooltip') as HTMLElement;
                copyIcon.addEventListener('click', () => {
                  this.copyInvalidPhoneList();
    
                  tooltip.style.display = 'block';
                  setTimeout(() => {
                    tooltip.style.display = 'none';
                  }, 2000);
                });
              }
            }
          }).then(result => {
            if (result.isDenied || result.isDismissed) {
              this.cancelled2.emit(true);
            } else if (result) {
              this.confirmed.emit(true);
            }
          });

          // SKIP default dialog
          return;
        }
        else if (this.data['reassign_reward'] !== undefined && this.data.reassign_reward) {
          // Custom swal-alert for Reassign Reward popup
          title = 'The member is not eligible to the promotion';
          Swal.fire({
            title: title,
            html: messages,
            icon: 'warning',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Proceed',
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonColor: '#d33',
            cancelButtonText: 'Cancel'
          }).then(result => {
            if (result.isConfirmed) {
              this.reassign.emit(true);
            } else if (result.isDenied || result.isDismissed) {
              this.cancelled.emit(true);
            }
          });

          // SKIP default dialog
          return;
        } 
        else if ((this.data['rebate_duplicateIDs'] && this.data['rebate_duplicateIDs'].length > 0) ) {
          title = 'Successfully Released Rebates';
          icon = 'info';
          iconColor = '#f27474';
          const copyIcon1 = `<i class="fas fa-copy copy-icon copy-rebate-exist-records"></i><div class="swal2-copy-tooltip copy-rebate-exist-tooltip">Copied!</div>`;
          let data = `<div class="text-left mx-2 mt-3">Duplicate Rebate IDs: ${copyIcon1}</div><div class="failed-records-section p-2 m-2"><ul>`;
          this.data['rebate_duplicateIDs'].map(row => data += `<li>${row}</li>`);
          data += '</ul></div><ul><li>Please review these duplicate rebate entries for further action</li></ul>';

          messages += data;

        }else {
          if (this.data['custom_affiliate_swal'] == true) {
            if (this.data['failed_records'] && this.data['failed_records'].length == 0) {
              title = 'Successfully added ' + this.message.toString().split(" ", 1) + ' records';
              icon = 'success';
              messages = ''
            }
            else if (this.data['failed_records'] == undefined) {
              title = this.message;
              icon = 'success';
              messages = ''
            }
          } else if (this.data['custom_promotion_member_swal'] == true || this.data['custom_verification_setting_bank_swal'] == true) {
            if (this.data['exist_records'] && this.data['exist_records'].length == 0) {
              title = 'Successfully added ' + this.message.toString().split(" ", 1) + ' records';
              icon = 'success';
              messages = ''
            }
            else if (this.data['exist_records'] == undefined) {
              title = this.message;
              icon = 'success';
              messages = ''
            }
          } else if (this.data['custom_bank_receipt_check_duplicate_swal']) {
            title = 'Duplicated Entries Detected';
            icon = 'info';
            messages = '';
            confirmButtonText = 'Yes';
            showCancelButton = true;
            cancelButtonColor = '#d33';
            cancelButtonText = 'No';

            if (this.data['duplicate_transactions'].length > 0) {
              const copyIcon1 = `<i class="fas fa-copy copy-icon copy-receipt-exist-records"></i><div class="swal2-copy-tooltip copy-receipt-exist-tooltip">Copied!</div>`;
              let data = `<div class="text-left mx-2 mt-3">Existing Records: ${copyIcon1}</div><div class="failed-records-section p-2 m-2"><ul>`;
              this.data['duplicate_transactions'].map(row => data += `<li>${row}</li>`);
              data += '</ul></div><br /><p>Are you sure you want to proceeed with the bank receipt creation?</p>';

              messages += data;
            }
          }

          if (this.data['custom_leads_import_swal'] == true) {
            if (this.data['leads_exist'] && this.data['leads_exist'].length == 0 && this.data['member_exist'] && this.data['member_exist'].length == 0) {
              title = 'Successfully imported ' + this.message.toString().split(" ", 1) + ' records';
              icon = 'success';
              messages = ''
            }
            else if (this.data['leads_exist'] == undefined && this.data['member_exist'] == undefined) {
              title = this.message;
              icon = 'success';
              messages = ''
            }
          }
        }
      }

      if (message && message.length > 0 && message[0] == 'Successfully connected to the call.') {
        title = 'Call Connected';
      }

      Swal.fire({
        title: title ?? this.title,
        html: messages,
        icon: icon ? icon : this.icon,
        iconColor: iconColor ? iconColor : this.iconColor,
        confirmButtonText,
        showCancelButton,
        reverseButtons: true,
        cancelButtonColor,
        cancelButtonText,
        didOpen: () => {
          // Bind function to the icon
          const copyIcon = document.querySelector('.copy-failed-records') ? document.querySelector('.copy-failed-records') : document.querySelector('.copy-exist-records');
          const copyIcon1 = document.querySelector('.copy-leads-exist-records');
          const copyIcon2 = document.querySelector('.copy-member-exist-records');
          const copyIcon3 = document.querySelector('.copy-receipt-exist-records');
          const copyIcon4 = document.querySelector('.copy-rebate-exist-records');

          if (copyIcon) {
            const tooltip = document.querySelector('.swal2-copy-tooltip') as HTMLElement;
            copyIcon.addEventListener('click', () => {
              document.querySelector('.copy-failed-records') ? this.copyFailedRecords() : this.copyExistRecords();

              tooltip.style.display = 'block';
              setTimeout(() => {
                tooltip.style.display = 'none';
              }, 2000);
            });
          }

          if (copyIcon1) {
            const tooltip1 = document.querySelector('.copy-leads-exist-tooltip') as HTMLElement;
            copyIcon1.addEventListener('click', () => {
              this.copyLeadsExistRecords();

              tooltip1.style.display = 'block';
              setTimeout(() => {
                tooltip1.style.display = 'none';
              }, 2000);
            });
          }
          if (copyIcon2) {
            const tooltip2 = document.querySelector('.copy-member-exist-tooltip') as HTMLElement;
            copyIcon2.addEventListener('click', () => {
              this.copyMembersExistRecords();

              tooltip2.style.display = 'block';
              setTimeout(() => {
                tooltip2.style.display = 'none';
              }, 2000);
            });
          }

          if (copyIcon3) {
            const tooltip3 = document.querySelector('.copy-receipt-exist-tooltip') as HTMLElement;
            copyIcon3.addEventListener('click', () => {
              this.copyBankReceiptExistRecords();

              tooltip3.style.display = 'block';
              setTimeout(() => {
                tooltip3.style.display = 'none';
              }, 2000);
            });
          }

          if (copyIcon4) {
            const tooltip4 = document.querySelector('.copy-rebate-exist-tooltip') as HTMLElement;
            copyIcon4.addEventListener('click', () => {
              this.copyRebateExistRecords();

              tooltip4.style.display = 'block';
              setTimeout(() => {
                tooltip4.style.display = 'none';
              }, 2000);
            });
          }
        }
      }).then(result => {
        if (result.isDenied || result.isDismissed) {
          this.cancelled.emit(true);
        } else if (result) {
          this.confirmed.emit(true);
        }
      });
    }
  }

  copyInvalidLeadList() {
    const clipboardText = this.data['invalid_lead_list'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyFailedRecords() {
    const clipboardText = this.data['failed_records'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyExistRecords() {
    const clipboardText = this.data['exist_records'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyLeadsExistRecords() {
    const clipboardText = this.data['leads_exist'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyMembersExistRecords() {
    const clipboardText = this.data['member_exist'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyBankReceiptExistRecords() {
    const clipboardText = this.data['duplicate_transactions'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyRebateExistRecords() {
    const clipboardText = this.data['rebate_duplicateIDs'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  copyInvalidPhoneList() {
    const clipboardText = this.data['invalid_phone_list'].join('\n');
    this.clipboard.copy(clipboardText);
  }

  private getNormalizedMessages(message: any): string[] {
    const source = Array.isArray(message) ? message : (message != null ? [message] : []);
    return source.filter(m => m != null && m !== '');
  }

  private shouldShowBankAlert(): boolean {
    if (!this.data?.['bank_reactivation_alert']) {
      return false;
    }

    const alert = this.data['bank_reactivation_alert'];
    const bankReactivation = this.data?.['bank_reactivation'];
    const bankDeactivation = this.data?.['bank_deactivation'];

    return !!(alert?.messages?.length)
      || (bankReactivation?.blocked_count ?? 0) > 0
      || (bankReactivation?.reactivated_count ?? 0) > 0
      || (bankDeactivation?.deactivated?.length ?? 0) > 0
      || (bankDeactivation?.skipped?.length ?? 0) > 0;
  }

  private getBlockedBankAccountCount(bankReactivation: any): number {
    if (!bankReactivation) {
      return 0;
    }

    if (typeof bankReactivation.blocked_count === 'number') {
      return bankReactivation.blocked_count;
    }

    return (bankReactivation.blocked ?? []).reduce(
      (sum: number, group: any) => sum + (group.accounts?.length ?? 1),
      0
    );
  }

  private normalizeBlockedGroups(bankReactivation: any): any[] {
    const blocked = bankReactivation?.blocked ?? [];
    if (!blocked.length) {
      return [];
    }

    if (blocked[0]?.accounts) {
      return blocked;
    }

    const grouped = new Map<string, any>();
    blocked.forEach((bank: any) => {
      const username = bank.blocking_username ?? '';
      if (!grouped.has(username)) {
        grouped.set(username, {
          blocking_username: username,
          accounts: [],
        });
      }

      grouped.get(username).accounts.push({
        bank_name: bank.bank_name ?? '-',
        account_number: bank.account_number,
      });
    });

    return Array.from(grouped.values());
  }

  private buildBlockedReactivationHtml(blockedGroups: any[], blockedCount: number): string {
    const isMultiPlayer = blockedGroups.length > 1;
    const intro = isMultiPlayer
      ? "The following bank accounts can't be reactivated because they're still active on other player accounts:"
      : blockedCount === 1
        ? "The following bank account can't be reactivated because it's still active on another player account:"
        : "The following bank accounts can't be reactivated because they're still active on another player account:";

    let html = `<div class="text-center mb-3 px-2">${intro}</div>`;
    html += '<div class="bank-reactivation-blocked-list text-left mx-2 mb-3 p-3">';

    blockedGroups.forEach((group: any, index: number) => {
      if (index > 0) {
        html += '<hr class="bank-reactivation-group-divider">';
      }
      html += this.buildPlayerAccountSection(
        group.blocking_username ?? '',
        group.accounts ?? []
      );
    });

    html += '</div>';

    const footer = blockedCount === 1
      ? 'Deactivate it on the player account above first, then try again.'
      : isMultiPlayer
        ? 'Deactivate them on the player accounts above first, then try again.'
        : 'Deactivate them on the player account above first, then try again.';

    html += `<div class="text-center px-2">${footer}</div>`;
    return html;
  }

  private buildBlockedReactivationFromPlainMessages(messages: string[]): string | null {
    const groups = this.parseBlockedGroupsFromAlertMessages(messages);
    if (!groups.length) {
      return null;
    }

    const blockedCount = groups.reduce(
      (sum: number, group: any) => sum + (group.accounts?.length ?? 0),
      0
    );

    return this.buildBlockedReactivationHtml(groups, blockedCount);
  }

  private buildBlockedReactivationFromAlertMessages(alertMessages: string[], blockedCount: number): string {
    const groups = this.parseBlockedGroupsFromAlertMessages(alertMessages);
    const intro = blockedCount === 1
      ? "The following bank account can't be reactivated because it's still active on another player account:"
      : "The following bank accounts can't be reactivated because they're still active on other player accounts:";

    if (!groups.length) {
      return `<div class="text-center mb-3 px-2">${intro}</div>${this.buildAlertMessagesList(alertMessages)}`;
    }

    return this.buildBlockedReactivationHtml(groups, blockedCount);
  }

  private parseBlockedGroupsFromAlertMessages(alertMessages: string[]): any[] {
    const fullLinePattern = /This bank account \[([^\]]+)\]\s*—\s*(?:Account\s+No\.|No\.)\s*\[([^\]]+)\][^.]*player account \[([^\]]+)\]/i;
    const groups: any[] = [];

    alertMessages.forEach((message: string) => {
      const match = message.match(fullLinePattern);
      if (match) {
        groups.push({
          blocking_username: match[3],
          accounts: [{
            bank_name: match[1],
            account_number: match[2],
          }],
        });
      }
    });

    return groups;
  }

  private buildDeactivationHtml(
    deactivatedAccounts: any[],
    skippedGroups: any[],
    alertMessages: string[]
  ): string {
    let html = '';
    let messageIndex = 0;

    if (deactivatedAccounts.length) {
      html += this.buildBankAccountBox(deactivatedAccounts);
      if (alertMessages[messageIndex]) {
        html += this.buildBankAlertMessage(alertMessages[messageIndex]);
        messageIndex++;
      }
    }

    if (skippedGroups.length) {
      skippedGroups.forEach((group: any) => {
        html += this.buildBankAccountBox(group.accounts ?? []);
      });
      if (alertMessages[messageIndex]) {
        html += this.buildBankAlertMessage(alertMessages[messageIndex]);
        messageIndex++;
      }
    }

    const remainingMessages = alertMessages.slice(messageIndex);
    if (remainingMessages.length) {
      html += this.buildAlertMessagesList(remainingMessages);
    }

    return html;
  }

  private formatBankAccountLine(account: any): string {
    return `<span class="bank-reactivation-account-bullet">&bull;</span><span class="bank-reactivation-account-text">${account.bank_name} &mdash; ${account.account_number}</span>`;
  }

  private buildPlayerAccountSection(username: string, accounts: any[]): string {
    if (!accounts.length) {
      return '';
    }

    let html = `<div class="bank-reactivation-player-section">`;
    if (username) {
      html += `<div class="bank-reactivation-player-label">PLAYER ACCOUNT &bull; ${username}</div>`;
    }
    html += '<ul class="bank-reactivation-account-list">';
    accounts.forEach((account: any) => {
      html += `<li class="bank-reactivation-account-item">${this.formatBankAccountLine(account)}</li>`;
    });
    html += '</ul></div>';

    return html;
  }

  private buildBankAccountBox(accounts: any[]): string {
    if (!accounts?.length) {
      return '';
    }

    let html = '<div class="bank-reactivation-blocked-list text-left mx-2 mb-3 p-3"><ul class="bank-reactivation-account-list">';
    accounts.forEach((account: any) => {
      html += `<li class="bank-reactivation-account-item">${this.formatBankAccountLine(account)}</li>`;
    });
    html += '</ul></div>';

    return html;
  }

  private buildBankAlertMessage(message: string): string {
    return `<div class="text-center px-2 mb-2">${message}</div>`;
  }

  private buildAlertMessagesList(messages: string[]): string {
    if (!messages.length) {
      return '';
    }

    let html = '<ul class="bank-reactivation-messages text-left mb-0 pl-3">';
    messages.forEach((msg: string) => {
      html += `<li>${msg}</li>`;
    });
    html += '</ul>';

    return html;
  }
}
